import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { api } from 'lib/request/api';
import type { SectionVertex } from './types';

/**
 * Wells near the section line, for the picker. Its own endpoint because "nearest first"
 * ranks distance from a polyline, which Elasticsearch cannot sort by — so paging and
 * order come from PostGIS. Name and uwi lookup still goes through the map's search.
 */

/** Query-key prefix — one cache entry per distinct sweep. */
export const NEARBY_WELLS_QUERY_KEY = 'cross-section:nearby-wells';

/** The columns the backend will sort on. */
export type NearbySort =
  'offset_ft' | 'name' | 'operator' | 'total_depth_ft' | 'spud_date';

export interface NearbyWell {
  uwi: string;
  name: string | null;
  operator: string | null;
  /**
   * Total depth, resolved from the survey stick (surface minus the toe's TVDSS) —
   * the same depth the section itself draws, so the picker and the plot agree. It is
   * not measured depth, so it must never be labelled MD.
   */
  totalDepthFt: number | null;
  spudDate: string | null;
  county: string | null;
  status: string | null;
  /** Distance to the nearest point on the section line, in feet. */
  offsetFt: number | null;
}

export interface NearbyWellsRequest {
  vertices: SectionVertex[];
  radiusFt: number;
  /** Narrows the sweep by name or uwi, server-side. */
  text?: string;
  /** Wells already on the section, so the picker never offers one twice. */
  excludeUwis?: string[];
  /**
   * Narrows the sweep to vertical holes — what a Well to Well section can accept, since
   * `CrossSection::VerticalEligibility` gates every vertex. Always sent rather than left
   * to the backend's default: a request that says what it wants survives a default change.
   */
  verticalOnly: boolean;
  sortAttribute?: NearbySort;
  page?: number;
  pageSize?: number;
}

interface NearbyWellsResponse {
  results: Array<{ id: string; type: string; value: NearbyWell }>;
  types: string[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/** What the controller sends back on a refused sweep: one message, not a list. */
interface NearbyErrorBody {
  error?: string;
}

/**
 * Prefers the backend's own words for a refused sweep, the way the cut does.
 *
 * @param {unknown} requestError - Whatever ky rejected with.
 * @return {Promise<Error>} The error to surface.
 */
const toNearbyError = async (requestError: unknown): Promise<Error> => {
  if (requestError instanceof HTTPError) {
    const body = await requestError.response
      .json<NearbyErrorBody>()
      .catch(() => null);
    if (body?.error) {
      return new Error(body.error);
    }
  }
  return requestError instanceof Error
    ? requestError
    : new Error('Could not search for wells near the section.');
};

/**
 * The sweep's cache key. Every input is in it, so a page turn or a sort is its own
 * entry and going back to one already seen costs nothing.
 *
 * @param {NearbyWellsRequest | null} request - The sweep, if there is one.
 * @return {Array} A stable key.
 */
export const nearbyWellsQueryKey = (request: NearbyWellsRequest | null) => {
  if (request === null) {
    return [NEARBY_WELLS_QUERY_KEY, 'idle'] as const;
  }
  return [
    NEARBY_WELLS_QUERY_KEY,
    request.vertices,
    request.radiusFt,
    request.text ?? '',
    request.excludeUwis ?? [],
    request.verticalOnly,
    request.sortAttribute ?? 'offset_ft',
    request.page ?? 1,
    request.pageSize ?? 100,
  ] as const;
};

/**
 * Runs a sweep, or sits idle until there is one to run.
 *
 * @param {NearbyWellsRequest | null} request - The sweep to run, or null.
 * @return {object} The page of wells, with its total and its state.
 */
export function useNearbyWells(request: NearbyWellsRequest | null) {
  const fetchable = request !== null && request.vertices.length >= 2;

  const { data, isFetching, error } = useQuery<NearbyWellsResponse, Error>({
    queryKey: nearbyWellsQueryKey(request),
    queryFn: async () => {
      try {
        return await api
          .post('queries/cross_sections/nearby', {
            json: { query: request },
          })
          .json<NearbyWellsResponse>();
      } catch (requestError) {
        throw await toNearbyError(requestError);
      }
    },
    enabled: fetchable,
    // A sweep is deterministic in its line, radius, filter and page, and every one of
    // those is in the key.
    staleTime: 300_000,
    placeholderData: keepPreviousData,
  });

  // React Query serves the previous data (`placeholderData`) without consulting
  // `enabled`, so the outputs are gated on the request instead — otherwise closing the
  // picker would keep handing back the last sweep as though it were live.
  const held = fetchable ? data : undefined;

  return {
    wells: held?.results.map((row) => row.value) ?? [],
    totalCount: held?.totalCount ?? 0,
    page: held?.page ?? 1,
    pageSize: held?.pageSize ?? 100,
    isLoading: fetchable && isFetching && held === undefined,
    isFetching: fetchable && isFetching,
    error: fetchable ? (error ?? null) : null,
  };
}
